interface ValidationError {
  type: 'error';
  message: string;
  nodeId?: string;
  severity: 'high' | 'medium' | 'low';
  // Add these for better auto-fix
  errorType?: 'no_personas_assigned' | 'disconnected_phase' | 'missing_connections' | 'circular_dependency' | 'no_tools_assigned';
  phaseType?: string;
  phaseName?: string;
}

interface ValidationWarning {
  type: 'warning';
  message: string;
  nodeId?: string;
  severity: 'high' | 'medium' | 'low';
  // Add these for better auto-fix
  errorType?: 'no_personas_assigned' | 'disconnected_phase' | 'missing_connections' | 'no_tools_assigned';
  phaseType?: string;
  phaseName?: string;
}

interface WorkflowMetrics {
  complexity: number; // 1-10 scale
  estimatedDuration: number; // minutes
  tokenBudget: number;
  parallelizability: number; // 0-1 scale
  criticalPathLength: number;
  nodeCount: { phases: number; tools: number; personas: number; artifacts: number };
}

interface WorkflowValidation {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  metrics: WorkflowMetrics;
}

export const validateWorkflow = (workflow: any): WorkflowValidation => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  console.log('🔍 VALIDATION-DEBUG: Starting validation for workflow:', {
    name: workflow?.name,
    phaseCount: workflow?.phases?.length || 0,
    nodeCount: workflow?.nodes?.length || 0,
    connectionCount: workflow?.connections?.length || 0
  });

  if (!workflow.phases || workflow.phases.length === 0) {
    errors.push({
      type: 'error',
      message: 'Workflow must have at least one phase',
      severity: 'high',
      errorType: 'missing_connections'
    });
    return { 
      isValid: false, 
      errors, 
      warnings, 
      metrics: calculateWorkflowMetrics(workflow) 
    };
  }

  // Log all phases being checked
  console.log('🔍 VALIDATION-DEBUG: Checking phases:', workflow.phases?.map(p => ({
    id: p.id,
    label: p.label,
    type: p.type,
    personaCount: p.config?.personas?.length || 0,
    personas: p.config?.personas || []
  })));

  // Check for cycles first
  const hasCycles = detectCycles(workflow);
  if (hasCycles) {
    errors.push({
      type: 'error',
      message: 'Workflow contains circular dependencies',
      severity: 'high',
      errorType: 'circular_dependency'
    });
  }

  // Check each phase for persona assignments and connections
  workflow.phases?.forEach((phase, index) => {
    console.log(`🔍 VALIDATION-DEBUG: Checking phase ${index + 1}/${workflow.phases.length}: ${phase.id} (${phase.label})`);
    
    // Check personas - look at both config AND actual connections
    const configPersonas = phase.config?.personas || [];
    const connectedPersonas = workflow.connections?.filter(conn => 
      conn.target === phase.id && 
      conn.dependencyType === 'persona_assignment'
    ) || [];
    
    console.log(`🔍 VALIDATION-DEBUG: Phase ${phase.id} - config personas: ${configPersonas.length}, connected personas: ${connectedPersonas.length}`);
    
    // A phase has personas if EITHER config has them OR there are persona connections
    const hasPersonas = configPersonas.length > 0 || connectedPersonas.length > 0;
    
    if (!hasPersonas) {
      console.log(`🔍 VALIDATION-DEBUG: Phase ${phase.id} (${phase.label}) has NO personas (config: ${configPersonas.length}, connections: ${connectedPersonas.length}) - adding error`);
      errors.push({
        type: 'error',
        message: `Phase "${phase.label}" has no personas assigned`,
        nodeId: phase.id,
        severity: 'high',
        errorType: 'no_personas_assigned',
        phaseType: phase.type,
        phaseName: phase.label
      });
    } else {
      console.log(`🔍 VALIDATION-DEBUG: Phase ${phase.id} (${phase.label}) has personas - OK (config: ${configPersonas.length}, connections: ${connectedPersonas.length})`);
    }

    // Check tools - look at both config AND actual connections
    const configTools = phase.config?.tools || [];
    const connectedTools = workflow.connections?.filter(conn => 
      conn.target === phase.id && 
      conn.dependencyType === 'tool_assignment'
    ) || [];
    
    console.log(`🔍 VALIDATION-DEBUG: Phase ${phase.id} - config tools: ${configTools.length}, connected tools: ${connectedTools.length}`);
    
    // A phase has tools if EITHER config has them OR there are tool connections
    const hasTools = configTools.length > 0 || connectedTools.length > 0;
    
    if (!hasTools) {
      console.log(`🔍 VALIDATION-DEBUG: Phase ${phase.id} (${phase.label}) has NO tools (config: ${configTools.length}, connections: ${connectedTools.length}) - adding warning`);
      warnings.push({
        type: 'warning',
        message: `Phase "${phase.label}" has no tools assigned`,
        nodeId: phase.id,
        severity: 'medium',
        errorType: 'no_tools_assigned',
        phaseType: phase.type,
        phaseName: phase.label
      });
    } else {
      console.log(`🔍 VALIDATION-DEBUG: Phase ${phase.id} (${phase.label}) has tools - OK (config: ${configTools.length}, connections: ${connectedTools.length})`);
    }

    // Check for artifact flow connections (only for multi-phase workflows)
    if (workflow.phases.length > 1) {
      const hasIncomingFlow = workflow.connections?.some(conn => 
        conn.target === phase.id && 
        (conn.dependencyType === 'flow' || conn.dependencyType === 'output_artifact')
      );
      
      const hasOutgoingFlow = workflow.connections?.some(conn => 
        conn.source === phase.id && 
        (conn.dependencyType === 'flow' || conn.dependencyType === 'output_artifact')
      );
      
      // Determine if this is a start, middle, or end phase
      const isFirstPhase = !hasIncomingFlow && hasOutgoingFlow;
      const isLastPhase = hasIncomingFlow && !hasOutgoingFlow;
      const isMiddlePhase = hasIncomingFlow && hasOutgoingFlow;
      const isIsolated = !hasIncomingFlow && !hasOutgoingFlow;
      
      if (isIsolated) {
        warnings.push({
          type: 'warning',
          message: `Phase "${phase.label}" is not connected to the workflow flow`,
          nodeId: phase.id,
          severity: 'medium',
          errorType: 'disconnected_phase',
          phaseType: phase.type,
          phaseName: phase.label
        });
      }
    }
  });

  // Check for persona connection consistency
  workflow.phases?.forEach(phase => {
    const personas = phase.config?.personas || [];
    
    personas.forEach(personaAssignment => {
      const personaId = personaAssignment.persona_id;
      
      // Find the persona node
      const personaNode = workflow.nodes?.find(node => 
        node.type === 'persona' && 
        node.data?.personaId === personaId
      );
      
      if (personaNode) {
        // Check if there's a corresponding connection
        const hasConnection = workflow.connections?.some(conn =>
          conn.source === personaNode.id &&
          conn.target === phase.id &&
          conn.dependencyType === 'persona_assignment'
        );
        
        if (!hasConnection) {
          errors.push({
            type: 'error',
            message: `Persona "${personaId}" is assigned to phase "${phase.label}" but not connected`,
            nodeId: phase.id,
            severity: 'high',
            errorType: 'missing_connections',
            phaseType: phase.type,
            phaseName: phase.label
          });
        }
      }
    });
  });

  const metrics = calculateWorkflowMetrics(workflow);

  console.log('🔍 VALIDATION-DEBUG: Validation complete:', {
    isValid: errors.length === 0,
    errorCount: errors.length,
    warningCount: warnings.length,
    errors: errors.map(e => ({ message: e.message, nodeId: e.nodeId }))
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    metrics
  };
};

const detectCycles = (workflow: any): boolean => {
  const visited = new Set();
  const recursionStack = new Set();

  const hasCycleDFS = (nodeId: string): boolean => {
    if (recursionStack.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;

    visited.add(nodeId);
    recursionStack.add(nodeId);

    const connections = workflow.connections?.filter(conn => conn.source === nodeId) || [];
    for (const conn of connections) {
      if (hasCycleDFS(conn.target)) return true;
    }

    recursionStack.delete(nodeId);
    return false;
  };

  const allNodes = [
    ...(workflow.phases?.map(p => p.id) || []),
    ...(workflow.nodes?.map(n => n.id) || [])
  ];

  for (const nodeId of allNodes) {
    if (hasCycleDFS(nodeId)) return true;
  }

  return false;
};

const calculateWorkflowMetrics = (workflow: any): WorkflowMetrics => {
  const phaseCount = workflow.phases?.length || 0;
  const toolCount = workflow.nodes?.filter(n => n.type === 'tool').length || 0;
  const personaCount = workflow.nodes?.filter(n => n.type === 'persona').length || 0;
  const artifactCount = workflow.nodes?.filter(n => n.type === 'artifact').length || 0;
  const connectionCount = workflow.connections?.length || 0;

  // Calculate complexity (1-10 scale)
  const branchingFactor = connectionCount / Math.max(phaseCount, 1);
  const complexity = Math.min(10, Math.ceil(
    (phaseCount * 0.3) + 
    (branchingFactor * 2) + 
    (toolCount * 0.5) +
    (personaCount * 0.4)
  ));

  // Estimate duration (rough calculation)
  const baseTimePerPhase = 5; // minutes
  const personaMultiplier = 1 + (personaCount * 0.2);
  const toolMultiplier = 1 + (toolCount * 0.1);
  const estimatedDuration = Math.ceil(phaseCount * baseTimePerPhase * personaMultiplier * toolMultiplier);

  // Estimate token budget (rough calculation)
  const baseTokensPerPhase = 1000;
  const personaTokenMultiplier = 1 + (personaCount * 0.5);
  const tokenBudget = Math.ceil(phaseCount * baseTokensPerPhase * personaTokenMultiplier);

  // Calculate parallelizability (0-1 scale)
  const maxParallelPaths = calculateMaxParallelPaths(workflow);
  const parallelizability = Math.min(1, maxParallelPaths / Math.max(phaseCount, 1));

  // Calculate critical path length
  const criticalPathLength = calculateCriticalPathLength(workflow);

  return {
    complexity,
    estimatedDuration,
    tokenBudget,
    parallelizability,
    criticalPathLength,
    nodeCount: {
      phases: phaseCount,
      tools: toolCount,
      personas: personaCount,
      artifacts: artifactCount
    }
  };
};

const calculateMaxParallelPaths = (workflow: any): number => {
  // Simplified calculation - count nodes with no dependencies
  const dependentNodes = new Set(workflow.connections?.map(conn => conn.target) || []);
  const independentNodes = (workflow.phases || []).filter(phase => !dependentNodes.has(phase.id));
  return Math.max(1, independentNodes.length);
};

const calculateCriticalPathLength = (workflow: any): number => {
  // Simplified calculation - find longest path through the workflow
  const adjacencyList = new Map();
  
  // Build adjacency list
  workflow.connections?.forEach(conn => {
    if (!adjacencyList.has(conn.source)) {
      adjacencyList.set(conn.source, []);
    }
    adjacencyList.get(conn.source).push(conn.target);
  });

  // Find longest path using DFS
  const visited = new Set();
  let maxPath = 0;

  const dfs = (nodeId: string, currentPath: number): number => {
    if (visited.has(nodeId)) return currentPath;
    
    visited.add(nodeId);
    const neighbors = adjacencyList.get(nodeId) || [];
    
    if (neighbors.length === 0) {
      return currentPath + 1;
    }

    let maxFromHere = currentPath + 1;
    for (const neighbor of neighbors) {
      maxFromHere = Math.max(maxFromHere, dfs(neighbor, currentPath + 1));
    }
    
    visited.delete(nodeId);
    return maxFromHere;
  };

  const allNodes = workflow.phases?.map(p => p.id) || [];
  for (const nodeId of allNodes) {
    maxPath = Math.max(maxPath, dfs(nodeId, 0));
  }

  return maxPath;
};

export const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

export const formatTokens = (tokens: number): string => {
  if (tokens < 1000) return `${tokens}`;
  if (tokens < 1000000) return `${(tokens / 1000).toFixed(1)}K`;
  return `${(tokens / 1000000).toFixed(1)}M`;
};
